import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  DialogActionTrigger,
  HStack,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Heading,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import MDEditor from '@uiw/react-md-editor';
import React, { useEffect, useRef } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { JsonReadonlyBlock } from '#src/components/json-readonly-block';
import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { useColorMode } from '#src/components/ui/color-mode';
import { toaster } from '#src/components/ui/toaster';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { GET_AI_AUDIT_BY_ID } from '#src/graphql/queries/ai-audit';

const ASK_AI_AUDIT_PROCESSOR_ID = 'ask-ai-audit-prompt';

type AskAiAuditChatEntry = { role: 'user' | 'assistant'; content: string };

function formatAskAiAuditTranscriptCopy(
  entries: AskAiAuditChatEntry[],
): string {
  const lines: string[] = ['Ask AI audit transcript\n'];
  for (const e of entries) {
    const label = e.role === 'user' ? 'User' : 'Assistant';
    lines.push(`\n--- ${label} ---\n\n${e.content.trim()}`);
  }
  return lines.join('');
}

interface AskAiAuditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditInput: unknown;
  auditOutput: unknown;
}

function AskAiAuditModal({
  open,
  onOpenChange,
  auditInput,
  auditOutput,
}: AskAiAuditModalProps) {
  const { colorMode } = useColorMode();
  const [composer, setComposer] = React.useState('');
  const [chatEntries, setChatEntries] = React.useState<AskAiAuditChatEntry[]>(
    [],
  );
  const [executeProcessor, { loading }] = useMutation(EXECUTE_PROCESSOR);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatEntries]);

  const handleClose = React.useCallback(() => {
    setComposer('');
    setChatEntries([]);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = React.useCallback(async () => {
    const q = composer.trim();
    if (!q) return;
    const priorMessages = chatEntries;
    try {
      const { data } = await executeProcessor({
        variables: {
          input: {
            processorId: ASK_AI_AUDIT_PROCESSOR_ID,
            variables: {
              question: q,
              priorMessages,
              auditInput,
              auditOutput,
            },
          },
        },
      });
      const result = data?.executeProcessor;
      if (result?.success && result?.result != null) {
        const message =
          typeof result.result === 'object' && 'message' in result.result
            ? result.result.message
            : undefined;
        if (typeof message === 'string') {
          setChatEntries((prev) => [
            ...prev,
            { role: 'user', content: q },
            { role: 'assistant', content: message },
          ]);
          setComposer('');
        } else {
          throw new Error('Processor did not return a message');
        }
      } else {
        throw new Error(result?.error ?? 'Processor failed');
      }
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to get AI response',
        type: 'error',
      });
    }
  }, [auditInput, auditOutput, chatEntries, composer, executeProcessor]);

  const handleCopyTranscript = React.useCallback(() => {
    if (chatEntries.length === 0) return;
    const text = formatAskAiAuditTranscriptCopy(chatEntries);
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        toaster.create({
          title: 'Copied',
          description: 'Full transcript copied to clipboard',
          type: 'success',
        });
      })
      .catch(() => {
        toaster.create({
          title: 'Copy failed',
          description: 'Could not copy to clipboard',
          type: 'error',
        });
      });
  }, [chatEntries]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="xl"
    >
      <CenteredDialogContent
        maxW="min(90vw, 56rem)"
        minH="min(88vh, 42rem)"
        maxH="92vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <DialogHeader flexShrink={0}>
          <DialogTitle>Ask AI about this audit</DialogTitle>
        </DialogHeader>
        <DialogBody
          flex="1"
          minH={0}
          display="flex"
          flexDirection="column"
          gap={3}
          overflowY="auto"
        >
          <Text fontSize="sm" color="fg.muted">
            Chat about this logged AI interaction. The model sees the same input
            and output JSON shown on this page (large payloads may be truncated
            server-side). Follow-up messages include this conversation.
          </Text>
          <Box flex="1" minH="min(200px, 28vh)" overflowY="auto">
            <VStack gap={3} align="stretch" pb={1}>
              {chatEntries.length === 0 ? (
                <Text fontSize="sm" color="fg.muted" textAlign="center" py={6}>
                  No messages yet. Type below and send to start.
                </Text>
              ) : (
                chatEntries.map((entry, i) => (
                  <Box
                    key={i}
                    alignSelf={
                      entry.role === 'user' ? 'flex-end' : 'flex-start'
                    }
                    maxW="85%"
                  >
                    <Text fontSize="xs" color="fg.muted" mb={1}>
                      {entry.role === 'user' ? 'You' : 'Assistant'}
                    </Text>
                    <Box
                      p={3}
                      borderRadius="md"
                      bg={entry.role === 'user' ? 'bg.subtle' : 'bg.muted'}
                      borderWidth="1px"
                      borderColor="border"
                      overflowX="auto"
                    >
                      {entry.role === 'user' ? (
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {entry.content}
                        </Text>
                      ) : (
                        <Box
                          data-color-mode={colorMode}
                          fontSize="sm"
                          maxW="100%"
                        >
                          <MDEditor.Markdown source={entry.content} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))
              )}
              <Box ref={messagesEndRef} />
            </VStack>
          </Box>
          <ChatComposerRow
            autoGrow
            value={composer}
            onChange={setComposer}
            onSubmit={() => void handleSubmit()}
            placeholder="e.g., What model was requested? Summarize the error…"
            disabled={loading}
            loading={loading}
          />
        </DialogBody>
        <DialogFooter flexShrink={0}>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogActionTrigger>
          <Button
            variant="outline"
            onClick={handleCopyTranscript}
            disabled={chatEntries.length === 0}
            title="Copy full transcript"
          >
            Copy transcript
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}

export function AiAuditDetailPage() {
  const { id } = useParams<{ id: string }>();
  const auditId = id ?? '';
  const [askOpen, setAskOpen] = React.useState(false);

  const { data, loading, error, refetch } = useQuery(GET_AI_AUDIT_BY_ID, {
    variables: { id: auditId },
    skip: !auditId,
  });

  if (!auditId) {
    return (
      <Box>
        <Text color="fg.muted">Missing audit id.</Text>
        <Link asChild color="blue.600" mt={2} display="inline-block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Spinner />
        <Text mt={2}>Loading audit…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Text color="red.500">Error: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
        <Link asChild color="blue.600" mt={2} display="block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  const audit = data?.aiAudit;
  if (!audit) {
    return (
      <Box>
        <Heading size="md" mb={2}>
          Not found
        </Heading>
        <Text color="fg.muted">
          No audit row for id{' '}
          <Text as="span" fontFamily="mono">
            {auditId}
          </Text>
          .
        </Text>
        <Link asChild color="blue.600" mt={4} display="inline-block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  return (
    <VStack gap={6} align="stretch">
      <AskAiAuditModal
        open={askOpen}
        onOpenChange={setAskOpen}
        auditInput={audit.input}
        auditOutput={audit.output}
      />
      <Box>
        <Link asChild color="blue.600" mb={2} display="inline-block">
          <RouterLink to="/ai-audit">← AI audit log</RouterLink>
        </Link>
        <HStack
          justify="space-between"
          align="flex-start"
          flexWrap="wrap"
          gap={3}
        >
          <Box>
            <Heading size="lg" color="fg">
              Audit detail
            </Heading>
            <Text fontSize="sm" color="fg.muted" fontFamily="mono">
              {audit.id}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {new Date(audit.createdAt).toLocaleString()}
            </Text>
            <Text fontSize="sm" color="fg.muted" fontFamily="mono">
              {audit.endpoint}
            </Text>
            {audit.context != null && audit.context !== '' ? (
              <Text fontSize="sm" color="fg.muted" fontFamily="mono">
                context: {audit.context}
              </Text>
            ) : null}
          </Box>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => setAskOpen(true)}
            flexShrink={0}
          >
            Ask AI
          </Button>
        </HStack>
        <Text fontSize="sm" color="fg.muted" maxW="48rem" mt={2}>
          Input is the HTTP request body sent to OpenAI. For document AI edit,
          the{' '}
          <Text as="span" fontFamily="mono">
            model
          </Text>{' '}
          field there is the value used after the server validates{' '}
          <Text as="span" fontFamily="mono">
            variables.model
          </Text>
          . If that differs from what you picked in the UI, the choice was not
          on the allowlist or did not reach the processor. In Output,{' '}
          <Text as="span" fontFamily="mono">
            model
          </Text>{' '}
          is OpenAI&apos;s canonical id and may look like a dated snapshot (e.g.{' '}
          <Text as="span" fontFamily="mono">
            gpt-4o-2024-08-06
          </Text>
          ) even when the request used a short alias.
        </Text>
      </Box>
      <JsonReadonlyBlock title="Input" value={audit.input} />
      <JsonReadonlyBlock title="Output" value={audit.output} />
    </VStack>
  );
}
